import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Pagination from 'components/v2/Pagination';
import { DocumentMagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { humanize, titleize } from 'underscore.string';
import formatMoney from 'utils/formatters/Money';
import EditButton from 'components/v2/buttons/EditButton';
import Tooltip from 'components/v2/Tooltip';
import Badge from 'components/v2/Badge';
import ArrowUpIcon from 'components/v2/icons/ArrowUpIcon';
import ArrowDownIcon from 'components/v2/icons/ArrowDownIcon';
import InfoIcon from 'components/v2/icons/InfoIcon';
import {
  getFileExtensionFromUrl,
  getFileIconClass,
} from 'helpers/helper_functions';
import { CheckIcon } from '@heroicons/react/20/solid';
import XIcon from 'components/v2/icons/XIcon';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';

function renderInfoIcon() {
  return <InfoIcon />;
}

function formatTimeAgo(date) {
  return moment(date).fromNow(true);
}

function Link({ children, url }) {
  return (
    <a
      data-testid="a-1ae20c50"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div data-testid="div-0085e49f" className="flex items-center space-x-1">
        <span data-testid="span-3f75b65d" className="flex-1">
          {children}
        </span>
      </div>
    </a>
  );
}

function StatusBadge(status) {
  let badgeColor;
  switch (status) {
    case 'confirmed':
      badgeColor = 'green';
      break;
    case 'awaiting':
      badgeColor = 'yellow';
      break;
    case 'expired':
      badgeColor = 'gray';
      break;
    case 'declined':
      badgeColor = 'red';
      break;
    default:
  }
  return (
    <Badge dataTestId="Badge-54c68afe" color={badgeColor} size="large">
      {humanize(status)}
    </Badge>
  );
}

function renderAuditTrailButton(item, i, setRateAudit) {
  return (
    <button data-testid="button-08444114">
      <DocumentMagnifyingGlassIcon
        className="w-4 h-4 text-green"
        onClick={() => setRateAudit(item)}
      />
    </button>
  );
}

export default function AccessorialRatesList({
  items,
  perPage,
  page,
  pages,
  sortDirection,
  sortColumn,
  toggleSort,
  canUpdate,
  total,
  onPageClick,
  onEdit,
  onToggleCheck,
  onToggleCheckAll,
  setRateAudit,
}) {
  const { t } = useTranslation();

  function handlePageClick(selectedPage) {
    onPageClick(selectedPage);
  }

  function handleEditButtonClick(item, index) {
    return function () {
      onEdit(item, index);
    };
  }

  function renderHeader(title, sortOptions) {
    return (
      <div data-testid="div-c8af517e" className="flex whitespace-nowrap">
        {title}
        {sortOptions.sortable
          ? renderSortIcon(sortOptions.sort, sortOptions.sortColumn)
          : null}
      </div>
    );
  }

  function renderSortIcon(sort, title) {
    return sortDirection === 'ASC' ? (
      <div
        data-testid="div-02c48d2b"
        onClick={(e) => toggleSort('DESC', title)}
      >
        <ArrowUpIcon className="w-3 ml-2" />
      </div>
    ) : (
      <div data-testid="div-4efab8cd" onClick={(e) => toggleSort('ASC', title)}>
        <ArrowDownIcon className="w-3 ml-2" />
      </div>
    );
  }

  function renderDocumentButton(item, index) {
    const requiredDoc = item?.document;
    const url = requiredDoc ? requiredDoc?.signedUrl : '';
    const extension = getFileExtensionFromUrl(url) || '';

    if (requiredDoc && url) {
      return (
        <button data-testid="button-bf134fe2">
          <a
            data-testid="a-22184e64"
            className={getFileIconClass(extension)}
            href={url}
            target="_blank"
            rel="noreferrer"
          >
            <img data-testid="img-13911235" alt="" />
          </a>
        </button>
      );
    }
  }

  return (
    <div data-testid="div-34117e2b" className="overflow-x-auto">
      <Pagination
        dataTestId="Pagination-fa16e792"
        className="border-t-0"
        perPage={perPage}
        page={page}
        pages={pages}
        total={total}
        showCount
        paginationContainerClassName="mx-auto my-0 sticky right-2/4"
        onPageClick={handlePageClick}
      />
      <table data-testid="table-e552c064" className="default-table">
        <thead data-testid="thead-8464ca96">
          <tr data-testid="tr-ec68506f">
            <th data-testid="th-2383738b">{}</th>
            <th data-testid="th-3f55f758">{t('USER')}</th>
            <th data-testid="th-b3f21569">
              {renderHeader(t('CARRIER_SHIPPER').toUpperCase(), {
                sortColumn: 'name',
                sortable: true,
                sort: false,
              })}
            </th>
            <th data-testid="th-cf069277">
              {renderHeader(t('MARKET', { count: 1 }).toUpperCase(), {
                sortColumn: 'market',
                sortable: true,
                sort: false,
              })}
            </th>
            <th data-testid="th-a1895567">
              {renderHeader(t('ACCESSORIAL_TYPE').toUpperCase(), {
                sortColumn: 'accessorialtype',
                sortable: true,
                sort: false,
              })}
            </th>
            <th data-testid="th-3d3a8bb5">
              {renderHeader(t('ACCESS_CODE').toUpperCase(), {
                sortColumn: 'accesscode',
                sortable: true,
                sort: false,
              })}
            </th>
            <th data-testid="th-61d9f9f9">
              {renderHeader(t('RATE', { count: 1 }).toUpperCase(), {
                sortColumn: 'rate',
                sortable: true,
                sort: false,
              })}
            </th>
            <th data-testid="th-f7c7bf5b">
              {renderHeader(t('RATE_TYPE').toUpperCase(), {
                sortColumn: 'ratetype',
                sortable: true,
                sort: false,
              })}
            </th>
            <th data-testid="th-df920545">
              {renderHeader(t('UNIT', { count: 1 }).toUpperCase(), {
                sortColumn: 'units',
                sortable: true,
                sort: false,
              })}
            </th>
            <th data-testid="th-7fbb0b8d">{t('VALIDITY').toUpperCase()}</th>
            {featureEnabled(CONFIGURED_FEATURES?.AUTO_APPLY_ACCESSORIAL) && (
              <th data-testid="th-7fbb0c8c">
                {t('AUTO_APPLY_QUESTION')}{' '}
                <Tooltip
                  dataTestId="Tooltip-ff3e64e3"
                  id="rate-table-status"
                  body={renderInfoIcon}
                  position="top"
                >
                  {t('AUTO_APPLY_LIST_PAGE_NOTE')}
                </Tooltip>{' '}
              </th>
            )}
            <th data-testid="th-1fad8a8d">
              <div
                data-testid="div-2ae188ab"
                className="flex whitespace-nowrap"
              >
                {t('STATUS')}
                <Tooltip
                  dataTestId="Tooltip-ff3e64e3"
                  id="rate-table-status"
                  body={renderInfoIcon}
                  position="top"
                >
                  <div data-testid="div-9b70cbbc" style={{ fontSize: '10px' }}>
                    {t('USER_RATE_CONFIRMED')}
                    <br data-testid="br-9c6e06e7" />
                    {t('USER_CONFIRMATION_WAIT')}
                    <br data-testid="br-bb8d8379" />
                    {t('RATE_VALIDITY_EXPIRED')}
                    <br data-testid="br-90a94cd7" />
                    {t('USER_DECLINED_RATE_CARD')}
                  </div>
                </Tooltip>
              </div>
            </th>
            <th data-testid="th-4b453f37">{t('UPDATED')}</th>
            <th data-testid="th-945b1e65">{}</th>
          </tr>
        </thead>
        <tbody data-testid="tbody-53c24181">
          {items.map((item, i) => {
            return (
              <tr
                data-testid={`tr-fa90c161-${i}`}
                key={i}
                className={i === items.length - 1 ? 'border-b' : ''}
              >
                <td data-testid={`td-1f5a8819-${i}`}>
                  <EditButton
                    dataTestId={`EditButton-3a9a236e-${i}`}
                    id={`${item.id}`}
                    onClick={handleEditButtonClick(item, i)}
                  />
                </td>
                <td data-testid={`td-544a1034-${i}`}>
                  {item.customerType === 'carrier' ? (
                    <Badge
                      dataTestId={`Badge-69b0e151-${i}`}
                      color="blue"
                      size="large"
                      rounded
                    >
                      {titleize(t('CARRIER'))}
                    </Badge>
                  ) : (
                    <Badge
                      dataTestId={`Badge-af3dd493-${i}`}
                      color="green"
                      size="large"
                      rounded
                    >
                      {titleize(t('SHIPPER'))}
                    </Badge>
                  )}
                </td>
                <td data-testid={`td-caebbbc9-${i}`}>
                  <Link url={`/users/${item.customer}`}>{item.name}</Link>
                </td>
                <td data-testid={`td-277e3630-${i}`}>{item.market}</td>
                <td data-testid={`td-1652367b-${i}`}>{item.accessorialtype}</td>
                <td data-testid={`td-9bb334eb-${i}`}>{item.accesscode}</td>
                <td data-testid={`td-1807e732-${i}`}>
                  {formatMoney(item.rate, DEFAULT_CURRENCY)}
                </td>
                <td data-testid={`td-0ecde819-${i}`}>{item.ratetype}</td>
                <td data-testid={`td-d71a0cbc-${i}`}>{item.units}</td>
                <td
                  data-testid={`td-0e19b63e-${i}`}
                  className="text-black whitespace-nowrap"
                  style={{ fontSize: '10px' }}
                >
                  {t('FROM_DATE_DATA', {
                    date: moment(item.effectiveStart || null).format('L'),
                  })}
                  <br data-testid={`br-1fd51a76-${i}`} />
                  {t('TO_DATE_DATA', {
                    date: moment(item.effectiveEnd || null).format('L'),
                  })}
                </td>
                {featureEnabled(
                  CONFIGURED_FEATURES?.AUTO_APPLY_ACCESSORIAL,
                ) && (
                  <td data-testid={`td-0b19h63c-${i}`}>
                    {item.autoApply ? (
                      <CheckIcon className="text-green-cm h-5 w-5 mt-1" />
                    ) : (
                      <XIcon className="text-red w-4 h-4" />
                    )}
                  </td>
                )}
                <td data-testid={`td-986c9351-${i}`}>
                  {StatusBadge(item.status)}
                </td>
                <td
                  data-testid={`td-23e98ad6-${i}`}
                  className="whitespace-nowrap"
                >
                  {formatTimeAgo(item.updatedAt)}
                </td>
                <td data-testid={`td-b67b03ce-${i}`}>
                  <div
                    data-testid={`div-5d47930c-${i}`}
                    className="flex space-x-2 items-center"
                  >
                    {renderDocumentButton(item, i)}
                    {renderAuditTrailButton(item, i, setRateAudit)}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        dataTestId="Pagination-28e1a747"
        className="border-none"
        perPage={perPage}
        page={page}
        pages={pages}
        total={total}
        showCount
        paginationContainerClassName="mx-auto my-0 sticky right-2/4"
        onPageClick={handlePageClick}
      />
    </div>
  );
}
